import type { SupabaseClient } from "@supabase/supabase-js";

// Audit feed read model — public.management_audit_feed (recent admin / platform activity;
// actor_label is already sha-redacted server-side, no raw actor user ids; the view omits raw
// metadata while preserving event/resource context). Read-only.
//
// The view also carries six bounded PRD-001AE permission detail columns (migration 20260814120000),
// null on every event type but the two that write them. This generic mapper deliberately ignores
// them: they belong to one surface and are mapped, and re-bounded, by `permission-audit.ts`.

export interface AuditRow {
  auditEventId: string;
  actorLabel: string | null;
  eventType: string;
  resourceType: string | null;
  resourceId: string | null;
  createdAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function mapAuditRow(raw: Record<string, unknown>): AuditRow {
  return {
    auditEventId: asString(raw.audit_event_id) ?? "",
    actorLabel: asString(raw.actor_label),
    eventType: asString(raw.event_type) ?? "event",
    resourceType: asString(raw.resource_type),
    resourceId: asString(raw.resource_id),
    createdAt: asString(raw.created_at),
  };
}

const AUDIT_FEED_LIMIT = 50;

/** Fetch the account's audit feed, newest first. Throws on read error (caller shows error state). */
export async function fetchAuditFeed(
  client: SupabaseClient,
  accountId: string,
  limit: number = AUDIT_FEED_LIMIT,
): Promise<AuditRow[]> {
  const { data, error } = await client
    .from("management_audit_feed")
    .select("*")
    .eq("account_id", accountId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load the audit feed.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapAuditRow(row as Record<string, unknown>));
}

// --- Consecutive-run grouping (fidelity slice 3) ------------------------------------------

export interface GroupedAuditEntry {
  /** The latest row of the run - it leads the display. */
  row: AuditRow;
  /** 1 for singles; the run length for collapsed runs (>= 3). */
  count: number;
}

const GROUP_MIN_RUN = 3;

/**
 * Collapse consecutive runs of the same eventType + actorLabel into one entry with a count.
 * Rows arrive newest-first; the first row of a run (the latest) represents it. Runs shorter
 * than three stay as individual entries - grouping exists to tame scan storms, not to hide
 * distinct activity.
 */
export function groupAuditRows(rows: readonly AuditRow[]): GroupedAuditEntry[] {
  const entries: GroupedAuditEntry[] = [];
  let index = 0;
  while (index < rows.length) {
    const head = rows[index]!;
    let runEnd = index + 1;
    while (
      runEnd < rows.length &&
      rows[runEnd]!.eventType === head.eventType &&
      rows[runEnd]!.actorLabel === head.actorLabel
    ) {
      runEnd += 1;
    }
    const runLength = runEnd - index;
    if (runLength >= GROUP_MIN_RUN) {
      entries.push({ row: head, count: runLength });
      index = runEnd;
    } else {
      for (let single = index; single < runEnd; single += 1) {
        entries.push({ row: rows[single]!, count: 1 });
      }
      index = runEnd;
    }
  }
  return entries;
}
